/**
 * L'onglet Couverture d'une fiche de transporteur : ce qu'il possède, et ce
 * qu'il dessert.
 *
 * Deux sections dans un seul onglet, décidé le 03/09/2026 : c'est ce qu'on
 * regarde ENSEMBLE quand on cherche qui peut faire un transport — « un frigo
 * semi qui charge dans le 28 » est une seule question, pas deux.
 *
 * **Enregistrement en bloc**, choisi le même jour : on coche ce qu'on veut,
 * on clique Enregistrer, et l'API reçoit la liste complète qui remplace
 * l'ancienne. Un appel par case ferait cent appels et cent lignes au journal
 * des traces pour un seul geste, et une coupure laisserait la fiche à moitié
 * remplie. Prix accepté : à deux sur la même fiche, le dernier écrase le
 * premier.
 *
 * Ce composant ne fait que dessiner et rapporter les clics. Il ne valide rien,
 * n'appelle rien : l'écran tient l'état et parle à l'API. C'est ce qui permet
 * de le rendre et de le regarder dans n'importe quel état — référentiel
 * absent, rien de coché, envoi en cours.
 *
 * **Le matériel et les zones s'enregistrent SÉPARÉMENT**, par deux routes
 * distinctes. Un seul bouton pour les deux donnerait un enregistrement partiel
 * si l'un passait et l'autre non, sans moyen de le dire simplement. Chaque
 * section porte donc son bouton, et ne s'allume que si elle a changé.
 */

import { desservisDAbord, memesCodes, memesZones, parCarrosserie } from './annuaire.js';
import type { Couverture, Vehicule, Zone } from './api.js';

/** Ce que l'onglet tient pendant qu'on coche, avant d'enregistrer. */
export interface EditionCouverture {
  /** Les codes matériel cochés. */
  readonly vehicules: readonly string[];
  /** Les zones cochées. Une zone sans aucun sens n'y figure pas. */
  readonly zones: readonly Couverture[];
  /** Une des deux sections est-elle en cours d'envoi ? */
  readonly envoi: 'aucun' | 'vehicules' | 'zones';
  readonly refus: string | null;
}

interface Proprietes {
  /** Le référentiel commun, chargé une fois par l'écran. */
  readonly referentielVehicules: readonly Vehicule[];
  readonly referentielZones: readonly Zone[];
  /** Ce qui est enregistré aujourd'hui — sert à savoir ce qui a changé. */
  readonly vehiculesEnregistres: readonly string[];
  readonly zonesEnregistrees: readonly Couverture[];
  readonly edition: EditionCouverture;
  readonly quandVehicule: (code: string, coche: boolean) => void;
  readonly quandZone: (
    zone: string,
    sens: 'depart' | 'arrivee',
    coche: boolean,
  ) => void;
  readonly quandEnregistrerVehicules: () => void;
  readonly quandEnregistrerZones: () => void;
}

export function OngletCouverture({
  referentielVehicules,
  referentielZones,
  vehiculesEnregistres,
  zonesEnregistrees,
  edition,
  quandVehicule,
  quandZone,
  quandEnregistrerVehicules,
  quandEnregistrerZones,
}: Proprietes) {
  const coches = new Set(edition.vehicules);
  const parZone = new Map(edition.zones.map((z) => [z.zone, z]));

  const materielChange = !memesCodes(edition.vehicules, vehiculesEnregistres);
  const zonesChangees = !memesZones(edition.zones, zonesEnregistrees);
  const envoi = edition.envoi !== 'aucun';

  // Le référentiel n'est pas encore là : on ne dessine pas des cases vides,
  // qui donneraient à croire que ce transporteur n'a rien.
  if (referentielVehicules.length === 0 && referentielZones.length === 0) {
    return (
      <div className="barre-onglet">
        <p className="attente">Chargement des référentiels…</p>
      </div>
    );
  }

  return (
    <div className="barre-onglet">
      {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}

      <div className="section">
        <div className="section-titre">
          <span>Matériel</span>
          <span className="sourdine">{edition.vehicules.length} coché(s)</span>
        </div>

        {parCarrosserie(referentielVehicules).map((groupe) => (
          <div className="groupe-materiel" key={groupe.nom}>
            <div className="groupe-titre">{groupe.nom}</div>
            <div className="grille-materiel">
              {groupe.lignes.map((v) => (
                <label className="case" key={v.code}>
                  <input
                    type="checkbox"
                    checked={coches.has(v.code)}
                    disabled={envoi}
                    onChange={(e) => quandVehicule(v.code, e.target.checked)}
                  />
                  <span className="case-libelle">{v.libelle}</span>
                  <span className="case-code">{v.code}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="actions">
          <button
            className="bouton"
            type="button"
            disabled={envoi || !materielChange}
            onClick={quandEnregistrerVehicules}
          >
            {edition.envoi === 'vehicules'
              ? 'Enregistrement…'
              : 'Enregistrer le matériel'}
          </button>
        </div>
      </div>

      <div className="section">
        <div className="section-titre">
          <span>Zones desservies</span>
          <span className="sourdine">{edition.zones.length} département(s)</span>
        </div>

        {/* Départ et arrivée séparés, décidé le 03/09/2026 : beaucoup de
            transporteurs ne font qu'un sens, et une liste unique le ferait
            oublier. Décocher les deux retire la ligne — une ligne qui ne dit
            ni l'un ni l'autre ne dit rien, et la base la refuse. */}
        <div className="grille-zones">
          <div className="zone-entete">
            <span />
            <span className="sourdine">Départ</span>
            <span className="sourdine">Arrivée</span>
          </div>
          {desservisDAbord(referentielZones, zonesEnregistrees).map((d) => {
            const zone = parZone.get(d.code);
            return (
              <div className="zone-ligne" key={d.code}>
                <span className="zone-nom">
                  <span className="code">{d.code}</span> {d.nom}
                </span>
                <input
                  type="checkbox"
                  aria-label={`${d.nom} au départ`}
                  checked={zone?.depart ?? false}
                  disabled={envoi}
                  onChange={(e) => quandZone(d.code, 'depart', e.target.checked)}
                />
                <input
                  type="checkbox"
                  aria-label={`${d.nom} à l’arrivée`}
                  checked={zone?.arrivee ?? false}
                  disabled={envoi}
                  onChange={(e) => quandZone(d.code, 'arrivee', e.target.checked)}
                />
              </div>
            );
          })}
        </div>

        <div className="actions">
          <button
            className="bouton"
            type="button"
            disabled={envoi || !zonesChangees}
            onClick={quandEnregistrerZones}
          >
            {edition.envoi === 'zones' ? 'Enregistrement…' : 'Enregistrer les zones'}
          </button>
        </div>
      </div>
    </div>
  );
}
